import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Card from 'components/global/Card';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faBox, faCalendarAlt, faPeopleCarry, faShippingFast } from '@fortawesome/pro-solid-svg-icons';

import InfoLabel from './component/InfoLabel';
import { Container } from './styled/UndeliveredAttempt1.styled';

export const undeliveredAttempt1DefaultValues = [
	{
		type: 'color',
		value: '#f5f5f5',
		name: 'pageBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'navigationBackgroundColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'iconColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'contentTextColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'contentTitleColor'
	},

	{
		type: 'color',
		value: '#FFFFFF',
		name: 'pageHeaderColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'headerTextColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'headerBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'formBackgroundColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'buttonColor'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showDeliverySlotSelection'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showIcon'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnBottom'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnTop'
	}
];

/**
 * @author Burak Ak
 */

const UndeliveredAttempt1 = (props) => {
	const { deliveryCompany, orderDate, orderNumber, deliveryDate, propJson, getData } = props;

	const { showError, showSuccess } = useAlert();
	const schema = useMemo(
		() =>
			yup.object().shape({
				failedDeliveryComment: yup.string().nullable().required()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			failedDeliveryComment: props?.failedDeliveryComment
		}
	});
	const { handleSubmit, reset } = formMethods;

	useEffect(() => {
		reset({
			failedDeliveryComment: props.failedDeliveryComment
		});
	}, [props.failedDeliveryComment]);

	const onSubmit = async (data) => {
		try {
			const response = await openAxios.post(OPEN.SMS.UPDATE_UNDELIVERED_ATTEMPT_SMS_DETAILS, {
				otoOrderID: props.otoOrderID,
				...data
			});
			if (response.status === 200) {
				showSuccess();
				getData?.();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<FormProvider {...formMethods}>
			<Container>
				<Card
					padding={0}
					background
					border={0}
					shadowed
					rounded
					style={{
						backgroundColor: propJson?.formBackgroundColor
					}}>
					<Typography
						style={{
							background: propJson?.headerBackgroundColor,
							color: propJson?.headerTextColor,
							borderTopLeftRadius: '4px',
							borderTopRightRadius: '4px'
						}}
						variant="p1"
						className="py-3 text-center"
						fontWeight="700">
						{props?.failedDeliveryComment ? 'failedDeliveryThankYouHeading' : 'failedDeliveryHeading'}
					</Typography>
					<div className="mx-5 mt-4">
						<div className="row row-cols-1 row-cols-lg-2">
							{propJson?.showOrderNumber && (
								<InfoLabel
									label="orderNumber"
									value={orderNumber}
									labelFontWeight="500"
									rowMode
									icon={propJson?.showIcon && faBox}
									iconSize="x-large"
									labelColor={propJson?.contentTitleColor}
									valueColor={propJson?.contentTextColor}
									iconColor={propJson?.iconColor}
								/>
							)}
							<InfoLabel
								label="orderDate"
								value={orderDate}
								labelFontWeight="500"
								rowMode
								icon={propJson?.showIcon && faCalendarAlt}
								iconSize="x-large"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
						</div>
						<div className="row row-cols-1 row-cols-lg-2">
							<InfoLabel
								label="deliveryCompany"
								value={deliveryCompany}
								labelFontWeight="500"
								rowMode
								icon={propJson?.showIcon && faShippingFast}
								iconSize="x-large"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
							<InfoLabel
								label="deliveryDate"
								value={deliveryDate}
								labelFontWeight="500"
								rowMode
								icon={propJson?.showIcon && faPeopleCarry}
								iconSize="x-large"
								labelColor={propJson?.contentTitleColor}
								valueColor={propJson?.contentTextColor}
								iconColor={propJson?.iconColor}
							/>
						</div>
						<InfoLabel
							label="failedDeliveryInfoText"
							labelFontWeight="500"
							rowMode
							icon={propJson?.showIcon && faCircleInfo}
							iconSize="xxx-large"
							labelColor={propJson?.contentTitleColor}
							valueColor={propJson?.contentTextColor}
							iconColor={propJson?.iconColor}
							className="mt-4"
						/>
					</div>
					<div className="p-3 mt-5 mx-5">
						<StringInput
							label="yourComment"
							name="failedDeliveryComment"
							disabled={props.failedDeliveryComment}
						/>
						<div style={{ display: 'flex', justifyContent: 'center' }} className="mt-4">
							{!props.failedDeliveryComment && (
								<Button
									label="submit"
									variant="black"
									onClick={handleSubmit(onSubmit, (errors) => {
										AppLogger.log(errors);
									})}
									style={{ background: propJson?.buttonColor }}
									className="mt-3"
								/>
							)}
						</div>
					</div>
				</Card>
			</Container>
		</FormProvider>
	);
};
UndeliveredAttempt1.defaultProps = {
	pageBgColor: ' #F5F5F5',
	buttonColor: '#000000',
	headerTextColor: '#FFFFFF',
	headerBgColor: '#000000',
	contentTitleColor: '#000000',
	contentTextColor: '#000000',
	iconColor: ' #000000',
	showIcon: true,
	showOrderNumber: true
};

export default UndeliveredAttempt1;
